// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/InventarioEstoque.tsx
//  Listagem paginada de inventários
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ClipboardCheck, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useInventario } from '@/features/estoque/hooks/useInventario';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Input } from '@/components/ui/input';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;

const InventarioEstoque = () => {
  const navigate = useNavigate();
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [busca,         setBusca]         = useState('');
  const [situacao,      setSituacao]      = useState<'todos' | 'aberto' | 'processado'>('todos');
  // Busca, situação, período e ordem vão ao servidor.
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'data' | 'id'>('data', true, () => setPagina(1));

  const { inventarios, total, totalPaginas, isLoading, isFetching, deleteInventario } =
    useInventario({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada,
        processado: situacao === 'todos' ? undefined : situacao === 'processado',
        ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Inventário de Estoque</h1>
          <p className="text-muted-foreground mt-1">Contagens e conferências do estoque físico</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/estoque/inventario/novo')}>
          <Plus className="h-4 w-4" />Novo Inventário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="flex items-center gap-2 whitespace-nowrap">
              <ClipboardCheck className="h-5 w-5" />Inventários
            </CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº documento ou ID..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Data do inventário" />
              <Select value={situacao} onValueChange={(v) => { setSituacao(v as typeof situacao); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40" aria-label="Situação"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas as situações</SelectItem>
                  <SelectItem value="aberto">Em aberto</SelectItem>
                  <SelectItem value="processado">Processados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="id" ordenacao={ordenacao}>ID</CabecalhoOrdenavel>
                  <TableHead>Nº Documento</TableHead>
                  <CabecalhoOrdenavel coluna="data" ordenacao={ordenacao}>Data</CabecalhoOrdenavel>
                  <TableHead className="text-right">Total de Itens</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : inventarios.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhum inventário neste período — amplie o período para ver os anteriores'
                      : 'Nenhum inventário encontrado'}
                  </TableCell></TableRow>
                ) : (
                  inventarios.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">#{inv.id}</TableCell>
                      <TableCell>{inv.numDocumento || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell>{new Date(inv.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell className="text-right">{inv.totalItens}</TableCell>
                      <TableCell>
                        {inv.processado
                          ? <Badge className="bg-green-500/10 text-green-600">Processado</Badge>
                          : <Badge className="bg-blue-500/10 text-blue-600">Em Aberto</Badge>}
                      </TableCell>
                      <TableCell>{new Date(inv.dtCriacao).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {!inv.processado && (
                            <Button size="sm" variant="outline"
                              onClick={() => navigate(`/estoque/inventario/${inv.id}`)}>
                              Lançar / Processar
                            </Button>
                          )}
                          {!inv.processado && (
                            <Button size="sm" variant="ghost" onClick={() => setDeletingId(inv.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteInventario(deletingId); setDeletingId(null); } }}
        title="Excluir Inventário"
        description="Tem certeza que deseja excluir este inventário? Esta ação não pode ser desfeita."
      />
    </div>
  );
};

export default InventarioEstoque;
